<div class="location-selector flex relative fill grow">
  <fin-booking-widget-selector
    class="location-button"
    [class.flex--basis-half]="!singleLocation()"
    [class.flex-1]="singleLocation()"
    [class.location-button--full]="singleLocation()"
    [label]="originLabel() ?? ('bookingSearch.from.title' | finLocalization)"
    [disabled]="originDisabled() || disabled()"
    [position]="singleLocation() ? undefined : 'left'"
    [filled]="!!locationPair()?.origin"
    [placeholder]="originPlaceholder() ?? ('bookingSearch.boundSelection.originPlaceholder' | finLocalization)"
    [attr.data-testid]="'location-selector-origin'"
    [popoverContent]="popoverContent"
    [popoverOptions]="originPopoverOptions"
    (click)="handleOpen('origin')"
    (close)="handleClose()"
    data-testid="location-selector-origin"
    [widgetType]="SelectorType.ORIGIN"
  >
    <ng-container *ngTemplateOutlet="locationInfo; context: { location: locationPair()?.origin }" />
  </fin-booking-widget-selector>

  @if (!singleLocation() && showSeparator()) {
    <fcom-icon-button
      class="flex flex--center flex--self-center absolute location-switch"
      [icon]="{ name: SvgLibraryIcon.SWITCH_FLIGHTS }"
      [theme]="IconButtonTheme.LIGHT_SECONDARY_OUTLINE"
      [size]="IconButtonSize.MEDIUM"
      [aria]="{ label: 'bookingSearch.aria.fromToSwitch' | finLocalization }"
      (clickBtn)="locationsSwitched.emit()"
      data-testid="switch-locations"
    />
  }

  @if (!singleLocation()) {
    <fin-booking-widget-selector
      class="flex--basis-half location-button"
      [label]="'bookingSearch.to.title' | finLocalization"
      [disabled]="destinationDisabled() || disabled()"
      [position]="'right'"
      [filled]="!!locationPair()?.destination"
      [placeholder]="'bookingSearch.boundSelection.destinationPlaceholder' | finLocalization"
      [attr.data-testid]="'location-selector-destination'"
      [popoverContent]="popoverContent"
      [popoverOptions]="destinationPopoverOptions"
      (click)="handleOpen('destination')"
      (close)="handleClose()"
      data-testid="location-selector-destination"
      [widgetType]="SelectorType.DESTINATION"
    >
      <ng-container *ngTemplateOutlet="locationInfo; context: { location: locationPair()?.destination }" />
    </fin-booking-widget-selector>
  }
</div>

<ng-template #popoverContent>
  @let locationType = locationType$ | async;
  @if (locationType) {
    <div class="scroll rounded-large" [attr.data-testid]="locationType + '-popover-content'">
      <header class="popover-header fill relative flex--column ps-medium-y ps-medium-x">
        <div class="flex--nogrow flex--basis-header flex flex--row flex--center">
          <h2
            class="font-heading-4 flex-1 truncate overflow-hidden nordic-blue-900-text text-icon-primary font-title-medium"
            [attr.id]="'popover-' + originPopoverOptions.popoverID + '-title'"
          >
            {{
              originLabel() ??
                ('bookingSearch.' + (locationType === 'origin' ? 'from' : 'to') + '.title' | finLocalization)
            }}
          </h2>
          <button
            class="close-button flex--nogrow flex--self-stretch btn--no-outline focus-inset"
            [attr.title]="'actions.close' | finLocalization"
            [attr.aria-label]="'actions.close' | finLocalization"
            (click)="closePopover()"
          >
            <fcom-icon class="icon-medium" [name]="SvgLibraryIcon.CLOSE_DELETE" />
          </button>
        </div>
      </header>
      <ng-container *ngTemplateOutlet="locations; context: { locationType: locationType, popover: true }" />
    </div>
  }
</ng-template>

@if (modalOpen) {
  <fcom-modal
    [(open)]="modalOpen"
    [title]="
      originLabel() ??
      ('bookingSearch.' + ((locationType$ | async) === 'origin' ? 'from' : 'to') + '.title' | finLocalization)
    "
    [fitToContent]="false"
    [buttons]="null"
    (close)="handleClose()"
    data-testid="location-modal"
  >
    <ng-container *ngTemplateOutlet="locations; context: { locationType: (locationType$ | async), popover: false }" />
  </fcom-modal>
}

<ng-template #locations let-locationType="locationType" let-popover="popover">
  <div>
    <div
      class="search-form flex"
      [ngClass]="{
        'flex--middle pr-large-x ps-small-y': !popover,
        'flex--column ps-medium-x': popover,
      }"
    >
      <fcom-text-input
        class="flex-1"
        [id]="'locationSearch-input'"
        [parentForm]="locationSearchForm"
        [translationPrefix]="'bookingWidget.destinationSearch.' + (locationType === 'origin' ? 'from' : 'to')"
        [controlName]="'locationSearch'"
        [required]="false"
        [placeHolder]="
          (locationType === 'origin' && originSearchPlaceholder()) ||
          `bookingSearch.${locationType === 'origin' ? 'from' : 'to'}.inputPlaceholder`
        "
        [displayMargin]="false"
        [ariaOptions]="{ describedby: 'location-search-info' }"
        [disableAutoComplete]="true"
        (keydown.enter)="moveFocusToResults($event)"
        #searchInput
        data-testid="search-input"
        finFocus
        [autofocus]="true"
      />

      @if (showLocateMe() && locationType === 'origin') {
        <div class="locate-me flex flex--end mr-xxsmall-l">
          <fcom-button
            (clickBtn)="getUserLocation()"
            [text]="'bookingSearch.locateMe' | finLocalization"
            [icon]="{
              name: SvgLibraryIcon.GPS_LOCATION,
              position: IconPosition.RIGHT,
            }"
            [mode]="ButtonMode.INLINE"
            [theme]="ButtonTheme.LIGHT_TERTIARY_GHOST"
            [loading]="isGeoLocating$ | async"
            [aria]="{ loadingLabel: 'actions.loading' | finLocalization }"
            data-testid="locateMe"
          />
        </div>
      }

      <p id="location-search-info" class="is-vishidden">
        {{ 'bookingSearch.a11y.locationSearchDescription.' + locationType | finLocalization }}
      </p>
    </div>

    @if (showPreviousSearches() && (enablePersonalization$ | async) === true && (queryString$ | async)?.length === 0) {
      <fin-previous-searches
        (closeLocationModal)="handleClose()"
        (focusOnSearchInput)="moveFocusToSearchInput()"
        (startSearch)="startSearch.emit()"
        [isGlobalBookingWidget]="isGlobalBookingWidget()"
      />
    }

    @if (hasLocations$ | async) {
      <div
        class="grey-50-bg surface-disabled-bg rounded-small ps-medium-y"
        [ngClass]="{
          'pr-large-x': !popover,
          'ps-medium-x': popover,
        }"
        data-testid="location-list-header"
      >
        <h3
          class="font-body-1 nordic-blue-900-text text-icon-primary font-body-medium"
          [attr.id]="locationType + '-list-title'"
          [tabIndex]="-1"
          #locationListHeading
        >
          <span data-testid="location-list-title">
            {{
              (queryString$ | async)?.length > 0 || isAm()
                ? ((locationType === 'origin' ? 'bookingSearch.from.header' : 'bookingSearch.to.headerNoFrom')
                  | finLocalization)
                : ((locationType === 'origin' && !singleLocation()
                    ? 'bookingSearch.from.nearestAiport'
                    : 'trendingDestinations'
                  ) | finLocalization)
            }}
          </span>

          @if ((queryString$ | async)?.length > 0) {
            <span class="is-vishidden" data-testid="location-list-results-found">
              &nbsp;{{
                'bookingSearch.a11y.found.' + (locationType === 'origin' ? 'locations' : 'destinations')
                  | finLocalization: [(locationResults$ | async)?.length]
              }}</span
            >
          }
        </h3>
      </div>
    }

    @if ((isLoading$ | async) === false) {
      <ul
        class="flex flex--column"
        tabindex="-1"
        role="listbox"
        [attr.id]="'to-location-' + locationType"
        [attr.aria-labelledby]="locationType + '-list-title'"
        [attr.aria-activedescendant]="
          'to-location-' +
          locationType +
          ((this.locations$ | async)?.[0]?.isDefault ? '-default-' : '-') +
          (this.activeIndex$ | async)
        "
      >
        @if (locations$ | async; as locations) {
          @for (location of locations; track $index; let i = $index; let first = $first; let last = $last) {
            <li
              class="flex flex--middle flex--space-between focus-inset link-hover"
              [ngClass]="{
                'pr-large-x': !popover,
                'ps-medium-x': popover,
              }"
              [class.border-bottom]="!last"
              [class.nordic-blue-50-bg]="i === (activeIndex$ | async)"
              [class.surface-secondary]="i === (activeIndex$ | async)"
              [class.nordic-blue-50-border]="i === (activeIndex$ | async)"
              [class.border-primary]="i === (activeIndex$ | async)"
              [attr.id]="'to-location-' + locationType + (location?.isDefault ? '-default-' : '-') + i"
              role="none presentation"
            >
              <button
                class="flex flex--space-between flex--middle fill ps-medium-y"
                [tabindex]="((activeIndex$ | async) === undefined && first) || (activeIndex$ | async) === i ? 0 : -1"
                [attr.aria-selected]="i === (activeIndex$ | async)"
                role="option"
                (click)="setLocationOrStartNewSearch(location, locationType)"
                (keydown)="handleKeyDown($event, locations)"
                (focus)="handleInitialLocationItemFocus(i)"
                #locationItem
                data-testid="location-item"
              >
                <div class="flex--column">
                  <div class="font-body-1 capitalize location-title font-body-medium">
                    <ng-container *ngTemplateOutlet="locationInfo; context: { location: location }" />
                  </div>
                  <div
                    class="font-body-2 ps-xxsmall-y nordic-blue-900-text text-icon-primary align-left font-body-small"
                  >
                    <i class="flag--small flag-{{ location.countryCode }}"></i>&nbsp;{{ location.title }}
                  </div>
                </div>
                @if (!location.transportType?.includes(TransportType.Flight)) {
                  <div>
                    @if (location.transportType?.includes(TransportType.Train)) {
                      <fcom-icon class="icon-medium" [name]="SvgLibraryIcon.TRAIN" />
                    }
                    @if (location.transportType?.includes(TransportType.Bus)) {
                      <fcom-icon class="icon-medium" [name]="SvgLibraryIcon.BUS" />
                    }
                  </div>
                }
              </button>
            </li>
          }
        }
      </ul>
    }

    <div role="status" aria-live="assertive" [tabIndex]="-1">
      @if (
        (queryString$ | async)?.length > 0 && (isLoading$ | async) === false && (locationResults$ | async)?.length === 0
      ) {
        <p class="pr-large-x pr-medium-y">
          {{ 'bookingSearch.a11y.found.notFound' | finLocalization: [queryString$ | async] }}
        </p>
      }
    </div>

    @if (isLoading$ | async) {
      <fcom-loader class="pr-large" [loaderType]="LoaderTheme.EMBEDDED" />
    }
  </div>
</ng-template>

<ng-template #locationInfo let-location="location">
  @if (location) {
    <div class="flex fill" data-testid="location-info">
      <span class="overflow-hidden truncate">
        {{ location.cityName }}
      </span>
      @if (!isAm()) {
        <span>
          &nbsp;({{
            location.locationCode && location.type === LocationTypeCff.AIRPORT
              ? location.locationCode
              : ('bookingSearch.allAirportsLabel' | finLocalization)
          }})</span
        >
      }
      <span class="is-vishidden">&nbsp;{{ location.country }}.</span>
    </div>
  }
</ng-template>
